import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { BRAND_ENDPOINTS } from "@src/utility/constants/endpoints";
import { CURRENCY } from "@src/utility/constants/panel/common";

const DEFAULT_LIMIT = 10;

const useBrandDebitNotes = (brandId) => {
    const { axiosInstance, loading } = useAxios({ base: "brand", initialLoader: true });
    const { axiosInstance: invoicesAxiosInstance } = useAxios({ base: "brand" });
    const currency = CURRENCY;
    const [notes, setNotes] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 1 });

    const fetchNotes = async (page = 1, limit = DEFAULT_LIMIT) => {
        if (!brandId) return;
        try {
            const res = await axiosInstance.get(BRAND_ENDPOINTS.DEBIT_NOTES(brandId), { params: { page, limit } });
            const result = res.data || res;
            const items = result.items || result.data || result.results || [];
            setNotes(items.filter((note) => note?.status !== "draft"));
            setPagination({
                total     : result.total ?? items.length,
                page      : result.page ?? page,
                limit     : result.limit ?? limit,
                totalPages: result.totalPages ?? 1
            });
        } catch {
            setNotes([]);
            setPagination({ total: 0, page, limit, totalPages: 1 });
        }
    };

    useEffect(() => {
        fetchNotes(1, DEFAULT_LIMIT);
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchNotes(page, limit);
    const handlePerRowsChange = (limit) => fetchNotes(1, limit);

    const fetchInvoices = async (noteId) => {
        try {
            const res = await invoicesAxiosInstance.get(BRAND_ENDPOINTS.DEBIT_NOTE_INVOICES(brandId, noteId), { skipSessionRedirect: true });
            const result = res.data || res;
            return result.items || result.data || result.results || [];
        } catch {
            return [];
        }
    };

    // Confirmed: PATCH v1/accounts/{brandId}/debit-notes/{id}/status, {status, reason?}.
    const updateStatus = async (noteId, status, reason) => {
            await axiosInstance.patch(BRAND_ENDPOINTS.DEBIT_NOTE_STATUS(brandId, noteId), reason ? { status, reason } : { status });
            await fetchNotes(pagination.page, pagination.limit);
    };

    const reject = (note, reason) => updateStatus(note._id, "rejected", reason);

    // Two-step flow: acknowledge receipt first, then approve & pay offline.
    // Each step can be walked back one stage at a time for correction —
    // undoAcknowledge returns an acknowledged note to issued, withdrawApproval
    // returns an approved note to acknowledged. Neither is a full Ops recall.
    const acknowledge = (note) => updateStatus(note._id, "acknowledged");
    const approve = (note) => updateStatus(note._id, "approved");
    const undoAcknowledge = (note, reason) => updateStatus(note._id, "issued", reason);
    const withdrawApproval = (note, reason) => updateStatus(note._id, "acknowledged", reason);

    return {
        loading,
        currency,
        notes,
        pagination,
        fetchInvoices,
        reject,
        acknowledge,
        approve,
        undoAcknowledge,
        withdrawApproval,
        handlePageChange,
        handlePerRowsChange
    };
};

export default useBrandDebitNotes;
